import { Component, ElementRef, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal, untracked } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { FieldTree, form, required } from '@angular/forms/signals'

import { MatSnackBar } from '@angular/material/snack-bar'

import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { DragAndDropService } from '../../_jaettu-angular/service/drag-and-drop.service'
import { TiedostojenLataamisService } from '../../_jaettu-angular/service/tiedostojen-lataamis.service'

import { HolviImportFromTempFilePyynto, HolviImportVastaus, KirjauksienLuontityyppi } from 'app/_jaettu-lemonator/model/kirjanpito'
import { Asiakas, AsiakkaanMaksutapa } from '../../_jaettu-lemonator/model/asiakas'

import { FileSystemFileEntry } from '../../_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'

import { SahkoisenTiliotteenAlatyyppi } from '@lemon/core-firebase/model/tiliote'
import { DateService } from '@lemon/core/service/date.service'
import { HolviApiManualFetchLog, HolviStartTransactionFetchTyojono } from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/holvi-backend'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { HolviSharedUriService } from 'app/_jaettu-lemonator/service/holvi-shared-uri.service'
import { Observable, Subject, combineLatest, firstValueFrom, of as observableOf } from 'rxjs'
import { map, switchMap, takeUntil } from 'rxjs/operators'

interface MaksutapaLomake {
  valittu: AsiakkaanMaksutapa | null
}

interface HolviApiLomake {
  holviApiStartDate: Date | null
}

interface TilitapahtumahakuLomake {
  start: Date | null
  end: Date | null
}

interface DisplayedHolviApiManualFetchLog extends Pick<HolviApiManualFetchLog, 'created' | 'result' | 'extraInfo'> {
  kirjanpitajanNimi: string
}

@Component({
  templateUrl: './holvi-import.component.html',
  styleUrls: ['./holvi-import.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HolviImportComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef

  readonly apiLoadRunning = signal(false)
  apiLoadWasRunning: boolean = false
  loading: boolean = false
  isDragging: boolean = false
  readonly maksutavatLoaded = signal(false)
  maksutapaLomake: FieldTree<MaksutapaLomake>
  holviApiLomake: FieldTree<HolviApiLomake>
  tilitapahtumahakuLomake: FieldTree<TilitapahtumahakuLomake>

  valittuMaksutapaFromSelect: AsiakkaanMaksutapa
  readonly latausvirhe = signal('')
  holviMaksutavatObservable: Observable<AsiakkaanMaksutapa[]>
  asiakasObservable: Observable<Asiakas>
  donePercentageObservable: Observable<number>
  kasittelemattomiaObservable: Observable<number>
  driverObservable: Observable<boolean>

  readonly tiedostoaLadataan = signal(false)
  readonly tiedostoaKasitellaan = signal(false)

  displayedFetchLogsObservable: Observable<DisplayedHolviApiManualFetchLog[]>
  readonly fetchError = signal<string | null>(null)

  holviApiMaksutapaActiveObservable: Observable<boolean>

  holviApiLoadRunningObservable: Observable<{ running: boolean; transactionsLeft: number; holviTransactionsLeft: number }>

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    private _errorHandler: ErrorHandler,
    private _dndService: DragAndDropService,
    private _asiakasService: AsiakasService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _formValidationService: FormValidationService,
    private _kirjanpitoService: KirjanpitoService,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _dateService: DateService,
    private _holviSharedUriService: HolviSharedUriService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {}

  ngOnInit() {
    this.asiakasObservable = this._asiakasService.nykyinenAsiakasObservable

    const injector = this._injector

    this.maksutapaLomake = form(
      signal<MaksutapaLomake>({ valittu: null }),
      polku => {
        required(polku.valittu)
      },
      { injector },
    )

    const valittuKentta = this.maksutapaLomake.valittu
    this.holviApiLomake = form(
      signal<HolviApiLomake>({ holviApiStartDate: null }),
      polku => {
        required(polku.holviApiStartDate)
        validoiReaktiivisesti(polku.holviApiStartDate, this._holviApiStartValidator(valittuKentta))
      },
      { injector },
    )

    this.tilitapahtumahakuLomake = form(
      signal<TilitapahtumahakuLomake>({ start: null, end: new Date() }),
      polku => {
        required(polku.start)
        validoiReaktiivisesti(polku.start, this._endBeforeStartValidator)
        required(polku.end)
        validoiReaktiivisesti(polku.end, this._endBeforeStartValidator)
      },
      { injector },
    )

    arvonMuutokset(valittuKentta, { injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(valittuMaksutapa => {
        this.valittuMaksutapaFromSelect = valittuMaksutapa
      })

    this._dndService.isDraggingInWindowObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(dragging => {
      this.isDragging = dragging
    })

    this.holviMaksutavatObservable = this._asiakasService.nykyisenAsiakkaanJaKayttajienRoolienJaLaskunMaksutavatObservable.pipe(
      map(maksutavat => {
        if (maksutavat) {
          return maksutavat.filter(
            m =>
              m.kirjauksienLuontityyppi === KirjauksienLuontityyppi.HOLVI_SAHKOINEN_TILIOTE ||
              (m.kirjauksienLuontityyppi === KirjauksienLuontityyppi.SAHKOINEN_TILIOTE && m.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.HOLVI_API),
          )
        }
        return null
      }),
    )

    this.holviApiMaksutapaActiveObservable = this.holviMaksutavatObservable.pipe(
      map(holviMaksutavat => {
        return holviMaksutavat.findIndex(mt => mt.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.HOLVI_API && mt.aktiivinen) > -1
      }),
    )

    this.holviMaksutavatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(maksutavat => {
      this.maksutavatLoaded.set(true)
      if (maksutavat && !this.maksutapaLomake.valittu().value()) {
        const newMaksutapa = maksutavat.filter(
          m => m.kirjauksienLuontityyppi === KirjauksienLuontityyppi.SAHKOINEN_TILIOTE && m.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.HOLVI_API,
        )
        if (newMaksutapa.length > 0) {
          asetaArvo(this.maksutapaLomake.valittu, newMaksutapa[0])
        } else {
          const oldMaksutavat = maksutavat.filter(m => m.kirjauksienLuontityyppi === KirjauksienLuontityyppi.HOLVI_SAHKOINEN_TILIOTE)
          if (oldMaksutavat.length > 0) {
            asetaArvo(this.maksutapaLomake.valittu, oldMaksutavat[0])
          }
        }
      }
    })

    this.kasittelemattomiaObservable = this._kirjanpitoService.holviZipKasittelemattaCountObservable
    this.driverObservable = this._kirjanpitoService.holviZipDriverExistsObservable

    this.holviApiLoadRunningObservable = combineLatest([
      this._kirjanpitoService.tilitapahtumiaKasittelemattaCountObservable,
      this._kirjanpitoService.holviApiKasittelemattaCountObservable,
      this._kirjanpitoService.holviApiDriverExistsObservable,
    ]).pipe(
      map(([transactions, holviTransactions, holviRunning]) => {
        const running = holviRunning || transactions > 0 || holviTransactions > 0
        if (!running && this.apiLoadWasRunning) {
          this.apiLoadRunning.set(false)
          this.apiLoadWasRunning = false
        } else if (holviRunning || transactions > 0) {
          this.apiLoadRunning.set(true)
          this.apiLoadWasRunning = true
        }
        return { running: running, transactionsLeft: transactions, holviTransactionsLeft: holviTransactions }
      }),
    )

    const fetchLogsObservable = this.asiakasObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return observableOf<HolviApiManualFetchLog[]>([])
        }
        return this._firebase
          .firestoreCollection<HolviApiManualFetchLog>(this._holviSharedUriService.getManualTransactionFetchLogCollection(asiakas.avain))
          .listen()
          .pipe(
            map(logs => {
              return logs.sort((a, b) => b.created.toMillis() - a.created.toMillis())
            }),
          )
      }),
    )

    this.displayedFetchLogsObservable = combineLatest([fetchLogsObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable]).pipe(
      map(([logs, kirjanpitajatMap]) => {
        if (!logs?.length || !kirjanpitajatMap) {
          return []
        }
        const displayedLogs: DisplayedHolviApiManualFetchLog[] = []
        for (const log of logs) {
          const kirjanpitaja = kirjanpitajatMap.get(log.createdBy)
          displayedLogs.push({
            kirjanpitajanNimi: kirjanpitaja ? kirjanpitaja.etunimi + ' ' + kirjanpitaja.sukunimi : log.createdBy,
            created: log.created,
            result: log.result,
            extraInfo: log.extraInfo,
          })
        }

        return displayedLogs
      }),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  public fileOver() {
    this._dndService.setDragging(true)
  }

  public fileLeave() {
    this._dndService.setDragging(true)
  }

  // Load start when drag 'n dropped
  public async fileDrop(entries: FileDropEntry[]): Promise<void> {
    this._dndService.setDragging(false)
    this._lataaTiedosto(entries)
  }

  // Load start when clicked
  public lataa(event) {
    if (event.target.files) {
      const list: FileList = event.target.files
      const tiedostot: FileDropEntry[] = this._tiedostojenLataamisService.fileListToNgxFileDropEntries(list)
      this._lataaTiedosto(tiedostot)
    }
  }

  public async startHolviTransactionSync() {
    this.fetchError.set(null)

    if (!this.tilitapahtumahakuLomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.tilitapahtumahakuLomake)
      return
    }

    this.apiLoadRunning.set(true)
    this._ladataanService.aloitaLataaminen()

    try {
      const asiakasPromise = firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
      const kirjanpitajanTiedotPromise = this._kirjautunutKayttajaService.getKirjanpitajanTiedot()

      const [asiakas, kirjanpitajanTiedot] = await Promise.all([asiakasPromise, kirjanpitajanTiedotPromise])

      const tyojonoUri = this._holviSharedUriService.startHolviTransactionFetchTyojonoUri(asiakas.avain, this._firebase.firestoreCreateId())

      const startDate = this.tilitapahtumahakuLomake.start().value()
      const endDate = this.tilitapahtumahakuLomake.end().value()

      const tyojono: HolviStartTransactionFetchTyojono = {
        aloitettu: this._firebase.firestoreTimestampFromDate(new Date()),
        asiakasAvain: asiakas.avain,
        start: this._dateService.dateToNumber(startDate),
        end: this._dateService.dateToNumber(endDate),
        createdByAccountant: kirjanpitajanTiedot.uid,
      }
      await this._firebase.firestoreSetData(tyojonoUri, tyojono)
    } catch (err: any) {
      this.apiLoadRunning.set(false)
      this._errorHandler.handleError(err)
      this.fetchError.set('Holvi-tilitapahtumien lataamisen aloittaminen epäonnistui!')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private async _lataaTiedosto(tiedostot: FileDropEntry[]): Promise<any> {
    const voidaanLadata = await this.tiedostoVoidaanLadata(tiedostot)
    if (!voidaanLadata) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.maksutapaLomake)
      return
    }

    const asiakas = await firstValueFrom(this.asiakasObservable)

    this.tiedostoaLadataan.set(true)

    const tiedosto = tiedostot[0]
    const tiedostonAvain = this._firebase.firestoreCreateId()
    const file = await this._tiedostojenLataamisService.getFile(tiedosto.fileEntry as FileSystemFileEntry)

    const uploadUri = 'holvi_import/' + asiakas.avain + '/' + tiedostonAvain
    const uploadStatus = this._tiedostojenLataamisService.upload(this._firebase, uploadUri, file, 'holvi-temp-eu')

    this.donePercentageObservable = uploadStatus.donePercentage

    return uploadStatus.doneObservable.subscribe({
      next: done => {
        if (!done) {
          return
        }

        this.tiedostoaKasitellaan.set(true)
        const maksutapa = this.valittuMaksutapaFromSelect
        const pyynto: HolviImportFromTempFilePyynto = {
          asiakasAvain: asiakas.avain,
          maksutapa: maksutapa.tunniste,
          asiakasId: asiakas.asiakasId + '',
          tiedostonAvain: tiedostonAvain,
        }

        return this._firebase
          .functionsCall<HolviImportFromTempFilePyynto, HolviImportVastaus>('kirjanpitoHolviDistributeWorkFromZip', pyynto, { timeout: 540 * 1000 })
          .then(omatulos => {
            if (!omatulos.onnistui) {
              this.latausvirhe.set('Tiedoston lataamisessa oli virhe. Ota yhteys ylläpitoon. 1')
            } else {
              this._snackbar.open('Lataus onnistui', 'OK', { duration: 5000, verticalPosition: 'top' })
            }
            const htmlInput = this.fileInput.nativeElement as HTMLInputElement
            htmlInput.value = ''
            this.donePercentageObservable = null
            this.tiedostoaKasitellaan.set(false)
            this.tiedostoaLadataan.set(false)
          })
          .catch(error => {
            this.latausvirhe.set('Tiedoston lataamisessa oli virhe. Ota yhteys ylläpitoon. 2')
            this._errorHandler.handleError(error)
            this.donePercentageObservable = null
            this.tiedostoaKasitellaan.set(false)
            this.tiedostoaLadataan.set(false)
            const htmlInput = this.fileInput.nativeElement as HTMLInputElement
            htmlInput.value = ''
          })
      },
      error: error => {
        this.latausvirhe.set('Tiedoston lataamisessa oli virhe. Ota yhteys ylläpitoon. 3')
        this._errorHandler.handleError(error)
        this.donePercentageObservable = null
        this.tiedostoaKasitellaan.set(false)
        this.tiedostoaLadataan.set(false)
        const htmlInput = this.fileInput.nativeElement as HTMLInputElement
        htmlInput.value = ''
      },
    })
  }

  private async tiedostoVoidaanLadata(tiedostot: FileDropEntry[]): Promise<boolean> {
    this.latausvirhe.set('')
    if (!this.maksutapaLomake().valid()) {
      return false
    }
    if (tiedostot.length < 1) {
      this.latausvirhe.set('Annettu syöte ei ole kelvollinen tiedosto.')
      return false
    }

    if (tiedostot.length > 1) {
      this.latausvirhe.set('Voit ladata vain yhden tiedoston kerrallaan.')
      return false
    }

    const supportedFileTypes = ['zip']
    for (const tiedosto of tiedostot) {
      const file = tiedosto.fileEntry as FileSystemFileEntry

      const fileEnding = this._tiedostojenLataamisService.getFileEndingFromFileName(file.name)
      // const fileSize = file ? await this._tiedostojenLataamisService.getFileSize(file) : -1

      if (!fileEnding || supportedFileTypes.indexOf(fileEnding.toLowerCase()) < 0) {
        this.latausvirhe.set('Tiedoston tyyppi ei ole tuettu. Tuetut tyypit ovat: ' + supportedFileTypes.join(', ') + '.')
        return false
      }

      // const maxKoko = 10 * 1024 * 1024
      // if (fileSize > maxKoko) {
      //   const kokoMegatavuissaLokalisoitu = this._currencyService.formatoiDesimaali((fileSize / 1024 / 1024), 2, this._translationService.nykyinenKieli)
      //   const maxKokoLokalisoitu = this._currencyService.formatoiDesimaali((maxKoko / 1024 / 1024), 2, this._translationService.nykyinenKieli)
      //   this.latausvirhe = 'Ladattava tiedosto voi olla maksimissaan ' + maxKokoLokalisoitu + ' megatavua. (Tiedoston koko oli nyt ' + kokoMegatavuissaLokalisoitu + ' megatavua.)'
      //   return false
      // }
    }

    return true
  }

  async restart() {
    this._ladataanService.aloitaLataaminen()

    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)

    const pyynto: { asiakasId: number; asiakasAvain: string } = {
      asiakasAvain: asiakas.avain,
      asiakasId: asiakas.asiakasId,
    }
    return this._firebase
      .functionsCall<{ asiakasId: number; asiakasAvain: string }, void>('kirjanpitoRestartDistributeHolviImportWork', pyynto, { timeout: 540 * 1000 })
      .then(() => {
        this._ladataanService.lopetaLataaminen()
      })
      .catch(error => {
        this._errorHandler.handleError(error)
        this._ladataanService.lopetaLataaminen()
      })
  }

  // Reaktiivisen lomakkeen tapaan toinen päivämäärä luetaan seuraamatta, joten kenttä validoidaan uudelleen vain sen omasta muutoksesta
  private _endBeforeStartValidator: ValidatorFn = (): ValidationErrors => {
    const start = untracked(() => this.tilitapahtumahakuLomake?.start().value())
    const end = untracked(() => this.tilitapahtumahakuLomake?.end().value())

    if (!start && !end) {
      return null
    }

    if (start > end) {
      return { endBeforeStart: true }
    }
    return null
  }

  private _holviApiStartValidator(maksutapaKentta: FieldTree<AsiakkaanMaksutapa | null>): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const maksutapa = untracked(() => maksutapaKentta().value())
      if (!control.value || !maksutapa) {
        return null
      }

      const maksutapaStart = maksutapa.tilitapahtumatSynkronoidaanAlkaen
      const valittuLocal = this._dateService.dateToLocalDate(control.value)
      if (maksutapaStart && this._dateService.compareLocalDates(valittuLocal, '>', maksutapaStart)) {
        return { startBeforeMaksutapa: true }
      }

      return null
    }
  }
}
